import {AlertCircle, Check, Copy, Key, Mail, MapPin, Phone} from "lucide-react";
import {Fragment, useState} from "react";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Separator} from "@/components/ui/separator";
import {useProperty} from "@/src/hooks/properties";
import {useStoredMeProperties} from "@/src/modules/core/store/me";
import {Property} from "@/src/modules/properties/types";
import {formatPropertyAddressLine} from "@/src/modules/properties/lib/property-address";

import {Loader} from "../../core/components";

interface PropertyInfoModal {
  open: boolean;
  propertyId: number;
  onClose: () => void;
}

type GateCodes = {code: string; name: string}[];

export function PropertyInfoModal({open, propertyId, onClose}: PropertyInfoModal) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const meProperties = useStoredMeProperties();
  // const {isPending, property} = useProperty(19);
  const isPending = false;
  const property = meProperties.find((p) => p.id === propertyId)!;

  // if (!property) {
  //   onClose();
  //   return
  // };

  const copyToClipboard = async (code: string, name: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(name);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const gateCodes: GateCodes =
    property && property.gate_codes ? JSON.parse(property.gate_codes) : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        {isPending ? (
          <Loader className="h-[40vh]" />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">{property.name}</DialogTitle>
              <DialogDescription className="flex items-start gap-2 pt-1">
                <MapPin className="size-4 mt-0.5 flex-shrink-0" />
                <span>{formatPropertyAddressLine(property)}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Property Manager
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!property.property_managers.length ? (
                    <div>
                      <p className="font-normal text-sm text-foreground">No property managers</p>
                    </div>
                  ) : null}
                  {property.property_managers.map((manager) => (
                    <Fragment key={`manager-${manager.id}`}>
                      <div>
                        <p className="font-semibold text-foreground">
                          {manager.first_name} {manager.last_name}
                        </p>
                      </div>
                      <div className="grid gap-2">
                        <a
                          href={`mailto:${manager.email}`}
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Mail className="size-4" />
                          {manager.email}
                        </a>
                        {manager.telephone ? (
                          <a
                            href={`tel:${manager.telephone}`}
                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <Phone className="size-4" />
                            {manager.telephone}
                          </a>
                        ) : null}
                        {/* <a
                      href={`tel:${manager.}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                      <Phone className="size-4" />
                      {manager.phone}
                    </a> */}
                      </div>
                    </Fragment>
                  ))}
                </CardContent>
              </Card>

              {/* Parking Information */}
              {/* <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ParkingCircle className="h-5 w-5" />
                    Parking Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-foreground">{data.parking.notes}</p>
                  {data.parking.location && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <MapPin className="size-4" />
                      {data.parking.location}
                    </p>
                  )}
                </CardContent>
              </Card> */}

              {/* Gate Codes */}
              {gateCodes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Key className="h-5 w-5" />
                      Access Codes
                    </CardTitle>
                    <CardDescription>Click to copy codes to clipboard</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {gateCodes.map((gate, index) => (
                      <div key={index}>
                        {index > 0 && <Separator className="my-3" />}
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{gate.name}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(gate.code, gate.name)}
                            className="ml-4"
                          >
                            {copiedCode === gate.name ? (
                              <>
                                <Check className="size-4 mr-2" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="size-4 mr-2" />
                                {gate.code}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Pool Schedule */}
              {/* {data.poolSchedule && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Pool Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Days</span>
                      <span className="text-sm font-medium">{data.poolSchedule.days}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Hours</span>
                      <span className="text-sm font-medium">{data.poolSchedule.hours}</span>
                    </div>
                    {data.poolSchedule.notes && (
                      <>
                        <Separator className="my-2" />
                        <p className="text-sm text-muted-foreground">{data.poolSchedule.notes}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              )} */}

              {/* Emergency Contacts */}
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-5 w-5" />
                    Emergency Contacts
                  </CardTitle>
                  <CardDescription>Important contacts for emergencies</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {property.contact_telephones.map((contact, index) => (
                    <div key={`contact-${index}`}>
                      {index > 0 && <Separator className="my-3" />}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-sm">{contact.name}</p>
                            {/* <Badge
                              variant={contact.priority === "primary" ? "destructive" : "secondary"}
                              className="text-xs"
                            >
                              {contact.priority}
                            </Badge> */}
                          </div>
                          <p className="text-xs text-muted-foreground">{contact.type}</p>
                        </div>
                        <a
                          href={`tel:${contact.telephone}`}
                          className="text-sm font-mono font-semibold hover:text-primary transition-colors"
                        >
                          {contact.telephone}
                        </a>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
